<script setup lang="ts">
import { computed } from 'vue';
import { formatUsMmDdYy } from '@/utils/date';
import type {
  TrackVoteEntry,
  TrackVoteSummary,
} from '@/composables/api/usePlaylistSwitchingApi';
import { excludesTrack, summarizeCounts } from '@/components/TrackVotes/trackVoteDisplay';

/**
 * The body of the vote-detail popover — the per-vote record for one track.
 *
 * Following the legacy list (GVAdminOSX TrackListItemTableCellView.swift:184),
 * only entries that carry a comment are listed; value-only love/hate votes are
 * conveyed by the row glyph and the counts, not repeated here. Inert master rows
 * (`master_gate_off`) get a muted note so a reader understands why a master
 * comment does not affect this zone's playlist.
 */
const props = defineProps<{
  summary: TrackVoteSummary;
}>();

/** Entries the popover lists: those with a comment, newest first (already sorted). */
const commentedEntries = computed(() =>
  props.summary.entries.filter((entry) => entry.comment !== null),
);

const countsLine = computed(() => summarizeCounts(props.summary));

function voterLine(entry: TrackVoteEntry): string {
  const name = entry.voter.name?.trim();
  const who = name || entry.source_name?.trim() || 'Unknown';
  return entry.voter.is_current_user ? `${who} (you)` : who;
}

function metaLine(entry: TrackVoteEntry): string {
  const parts: string[] = [];
  if (entry.vote_type_name) parts.push(entry.vote_type_name);
  else if (entry.source_name) parts.push(entry.source_name);
  if (entry.vote_reason_name) parts.push(entry.vote_reason_name);
  return parts.join(' · ');
}

const valueLabel: Record<TrackVoteEntry['vote_label'], string> = {
  love: 'Loved',
  hate: 'Hated',
  comment: 'Comment',
  other: 'Vote',
};
</script>

<template>
  <div data-testid="track-votes-popover">
    <div class="mb-2 flex items-baseline justify-between gap-2">
      <span class="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        Votes &amp; comments
      </span>
      <span class="text-muted-foreground text-[11px] tabular-nums">{{ countsLine }}</span>
    </div>

    <p
      v-if="excludesTrack(summary)"
      class="text-destructive mb-2 text-[11px] leading-snug"
      data-testid="track-votes-excluded-note"
    >
      Excluded from generated playlists by a hate vote.
    </p>

    <ul v-if="commentedEntries.length" class="space-y-2.5">
      <li
        v-for="entry in commentedEntries"
        :key="entry.id"
        class="border-border/60 border-t pt-2 first:border-t-0 first:pt-0"
        :data-testid="`track-votes-entry-${entry.id}`"
      >
        <div class="text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px]">
          <span class="text-foreground font-medium">{{ valueLabel[entry.vote_label] }}</span>
          <span>·</span>
          <span>{{ voterLine(entry) }}</span>
          <span v-if="entry.created_at">·</span>
          <span v-if="entry.created_at" class="tabular-nums">{{
            formatUsMmDdYy(entry.created_at)
          }}</span>
          <span
            v-if="entry.origin === 'master'"
            class="bg-muted text-muted-foreground rounded px-1 py-0.5 text-[10px] leading-none font-medium"
            >Master</span
          >
          <span
            v-if="entry.is_internal"
            class="rounded bg-amber-500/20 px-1 py-0.5 text-[10px] leading-none font-medium text-amber-800 dark:text-amber-200"
            data-testid="track-votes-internal-chip"
            >Internal</span
          >
        </div>
        <p v-if="metaLine(entry)" class="text-muted-foreground mt-0.5 text-[11px] leading-snug">
          {{ metaLine(entry) }}
        </p>
        <p class="text-foreground mt-0.5 text-xs leading-snug break-words">{{ entry.comment }}</p>
        <p
          v-if="entry.excluded_reason === 'master_gate_off'"
          class="text-muted-foreground mt-0.5 text-[11px] italic"
          data-testid="track-votes-master-gate-note"
        >
          This zone does not include master-zone votes, so this note does not affect its playlist.
        </p>
      </li>
    </ul>

    <p v-else class="text-muted-foreground text-xs">No comments on this track.</p>
  </div>
</template>
